import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Box, Button, TextField, Typography, MenuItem, Card, CardContent, FormControlLabel, Switch, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Skeleton
} from '@mui/material';
import Grid from '@mui/material/GridLegacy'; 
import { matchApi } from '../../services/api';
import type { Match, MatchTeam, Team, MatchInning } from '../../models';
import GroupIcon from '@mui/icons-material/Group';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import MatchPlayersTable from './MatchPlayersTable';
import GroundAutocomplete from '../../components/Cricket/GroundAutocomplete';

const matchFormatOptions = [
  { value: 'T20', label: 'T20' },
  { value: 'T10', label: 'T10' },
  { value: 'ODI', label: 'ODI' },
  { value: 'TEST', label: 'Test' },
  { value: 'HUNDRED_BALL', label: 'Hundred' },
  { value: 'SIXES', label: 'Sixes' },
];

const statusOptions = [
  { value: 'Not Started', label: 'Not Started' },
  { value: 'In Progress', label: 'In Progress' },
  { value: 'Completed', label: 'Completed' },
  { value: 'Abandoned', label: 'Abandoned' },
  { value: 'Cancelled', label: 'Cancelled' },
];

// Memoized form components for better performance
const MemoizedTextField = React.memo(TextField);
const MemoizedMenuItem = React.memo(MenuItem);


const FantasyPlatformIdsForm = ({
  matchId,
  initialValues,
}: {
  matchId: number;
  initialValues: {
    d_id: number;
    c_id: number;
    mt_id: number;
    f_id: number;
    v_id: number;
    e_id: number;
    e_o_id: number;
  };
}) => {
  const [values, setValues] = useState(initialValues);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);

  const handleChange = (field: keyof typeof values) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value === '' ? '' : Number(e.target.value);
    setValues((prev) => ({ ...prev, [field]: newValue }));
    setSuccess(false);
    setError(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await matchApi.patch(matchId, values);
      setSuccess(true);
    } catch (err) {
      setError('Failed to update fantasy platform IDs');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>Fantasy Platform IDs</Typography>
        <Grid container spacing={2}>
          {Object.entries(values).map(([key, value]) => (
            <Grid item xs={12} sm={6} key={key}>
              <TextField
                fullWidth
                size="small"
                label={key.replace('_id', '').toUpperCase() + ' ID'}
                type="number"
                value={value === 0 ? '' : value}
                onChange={handleChange(key as keyof typeof values)}
                helperText={`Match ID on ${key.replace('_id', '').toUpperCase()} platform`}
                disabled={saving}
              />
            </Grid>
          ))}
        </Grid>
        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>Save</Button>
        </Box>
        {saving && <Typography variant="body2" color="text.secondary">Saving...</Typography>}
        {success && <Typography variant="body2" color="success.main">Saved!</Typography>}
        {error && <Typography variant="body2" color="error">{error}</Typography>}
      </CardContent>
    </Card>
  );
};

const MatchView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [match, setMatch] = useState<Match | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [editingTeamId, setEditingTeamId] = useState<number | null>(null);
  const [editingTeamData, setEditingTeamData] = useState<Partial<MatchTeam>>({});
  const [teamSaveLoading, setTeamSaveLoading] = useState(false);
  const [teamSaveError, setTeamSaveError] = useState<string | null>(null);

  const [editDetails, setEditDetails] = useState<any>({});
  const [detailsSaving, setDetailsSaving] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [detailsSuccess, setDetailsSuccess] = useState(false);

  useEffect(() => {
    if (match) {
      setEditDetails({
        match_format: match.match_format,
        match_status: match.match_status,
        start_date: match.start_date,
        venue: match.venue ? { ...match.venue } : null,
        match_result: match.match_result,
        lineup_announced: match.lineup_announced,
        score_algorithm_enabled: match.score_algorithm_enabled,
        fp_available: match.fp_available,
      });
    }
  }, [match]);

  useEffect(() => {
    const fetchMatch = async () => {
      setLoading(true);
      try {
        const response = await matchApi.getById(Number(id));
        setMatch(response.data);
        setTeams(response.data.teams || []);
        setError(null);
      } catch (err) {
        setError('Failed to fetch match');
      } finally {
        setLoading(false);
      }
    };
    fetchMatch();
    // eslint-disable-next-line
  }, [id]);

  const handleDetailsChange = (field: string, value: any) => {
    setEditDetails((prev: any) => ({ ...prev, [field]: value }));
  };


  const handleDetailsSave = async () => {
    setDetailsSaving(true);
    setDetailsError(null);
    setDetailsSuccess(false);
    try {
      const payload = { ...editDetails };
      if (payload.venue && typeof payload.venue === 'object') {
        payload.venue = { id: payload.venue.id };
      }
      await matchApi.update(Number(id), payload);
      setMatch((prev) => prev ? { ...prev, ...payload } : prev);
      setDetailsSuccess(true);
    } catch (err) {
      setDetailsError('Failed to update match details');
    } finally {
      setDetailsSaving(false);
    }
  };

  // Memoized handlers for team editing
  const handleEditTeam = useCallback((mt: MatchTeam) => {
    setEditingTeamId(mt.id);
    setEditingTeamData({ ...mt });
    setTeamSaveError(null);
  }, []);

  const handleCancelEditTeam = useCallback(() => {
    setEditingTeamId(null);
    setEditingTeamData({});
    setTeamSaveError(null);
  }, []);

  const handleChangeEditTeam = useCallback((field: keyof MatchTeam, value: any) => {
    setEditingTeamData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSaveEditTeam = useCallback(async () => {
    if (!editingTeamId) return;
    setTeamSaveLoading(true);
    setTeamSaveError(null);
    try {
      await matchApi.updateMatchTeam(editingTeamId, editingTeamData);
      setMatch((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          matchteam_set: prev.matchteam_set.map((mt) =>
            mt.id === editingTeamId ? { ...mt, ...editingTeamData } : mt
          ),
        };
      });
      setEditingTeamId(null);
      setEditingTeamData({});
    } catch (err: any) {
      setTeamSaveError('Failed to save changes');
    } finally {
      setTeamSaveLoading(false);
    }
  }, [editingTeamId, editingTeamData]);

  const handleAddTeam = useCallback(() => {
    // Open dialog or navigate to add team page
  }, []);

  if (loading) return <Box sx={{ p: 3 }}><Skeleton variant="rectangular" height={300} /></Box>;
  if (error) return <Box sx={{ color: 'error.main', p: 2 }}>{error}</Box>;
  if (!match) return null;

  return (
    <Box sx={{ width: '100%', p: { xs: 1, md: 3 } }}>
      <Grid container spacing={3}>
        {/* Summary Card */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>{match.name || 'Match'}</Typography>
                <Button variant="outlined" component={Link} to="/matches">Back to Matches</Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                <Typography variant="body1">
                  <b>Series:</b>{' '}
                  <a href={`https://ai11.in/series/${match.series?.slug}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline', color: '#1976d2' }}>{match.series?.name}</a>
                  <IconButton href={`https://ai11.in/series/${match.series?.slug}`} target="_blank" rel="noopener noreferrer" size="small" color="primary"><OpenInNewIcon fontSize="small" /></IconButton>
                </Typography>
                <Typography variant="body1"><b>Status:</b> {match.match_status}</Typography>
                <Typography variant="body1"><b>Format:</b> {match.match_format}</Typography>
                <Typography variant="body1"><b>Date:</b> {new Date(match.start_date).toLocaleString()}</Typography>
                <Typography variant="body1">
                  <b>Venue:</b>{' '}
                  {match.venue ? (
                    <>
                      <a href={`https://ai11.in/ground/${match.venue.id}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline', color: '#1976d2' }}>{match.venue.name}</a>
                      <IconButton href={`https://ai11.in/ground/${match.venue.id}`} target="_blank" rel="noopener noreferrer" size="small" color="primary"><OpenInNewIcon fontSize="small" /></IconButton>
                    </>
                  ) : 'N/A'}
                </Typography>
                <Typography variant="body1">
                  <b>Teams:</b>{' '}
                  {teams.map((t, idx) => (
                    <span key={t.id} style={{ marginRight: 8 }}>{t.name}{idx < teams.length - 1 ? ' vs ' : ''}</span>
                  ))}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Editable Details Section */}
        <Grid item xs={12} md={7}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>Edit Match Details</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <MemoizedTextField
                    fullWidth
                    name="match_format"
                    label="Match Format"
                    select
                    value={editDetails.match_format || ''}
                    onChange={e => handleDetailsChange('match_format', e.target.value)}
                  >
                    {matchFormatOptions.map((option) => (
                      <MemoizedMenuItem key={option.value} value={option.value}>{option.label}</MemoizedMenuItem>
                    ))}
                  </MemoizedTextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MemoizedTextField
                    fullWidth
                    name="match_status"
                    label="Status"
                    select
                    value={editDetails.match_status || ''}
                    onChange={e => handleDetailsChange('match_status', e.target.value)}
                  >
                    {statusOptions.map((option) => (
                      <MemoizedMenuItem key={option.value} value={option.value}>{option.label}</MemoizedMenuItem>
                    ))}
                  </MemoizedTextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MemoizedTextField
                    fullWidth
                    name="start_date"
                    label="Start Date"
                    type="datetime-local"
                    value={editDetails.start_date || ''}
                    onChange={e => handleDetailsChange('start_date', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <GroundAutocomplete
                    value={editDetails.venue || null}
                    onChange={venue => handleDetailsChange('venue', venue)}
                    error={false}
                    helperText={''}
                    disabled={detailsSaving}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MemoizedTextField
                    fullWidth
                    name="match_result"
                    label="Result"
                    value={editDetails.match_result || ''}
                    onChange={e => handleDetailsChange('match_result', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={<Switch checked={!!editDetails.lineup_announced} onChange={e => handleDetailsChange('lineup_announced', e.target.checked)} name="lineup_announced" />}
                    label="Lineup Announced"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={<Switch checked={!!editDetails.score_algorithm_enabled} onChange={e => handleDetailsChange('score_algorithm_enabled', e.target.checked)} name="score_algorithm_enabled" />}
                    label="Score Algorithm Enabled"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={<Switch checked={!!editDetails.fp_available} onChange={e => handleDetailsChange('fp_available', e.target.checked)} name="fp_available" />}
                    label="FP Available"
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button variant="contained" color="primary" onClick={handleDetailsSave} disabled={detailsSaving}>Save</Button>
                <Button variant="outlined" onClick={() => navigate(-1)}>Back</Button>
              </Box>
              {detailsSaving && <Typography variant="body2" color="text.secondary">Saving...</Typography>}
              {detailsSuccess && <Typography variant="body2" color="success.main">Saved!</Typography>}
              {detailsError && <Typography variant="body2" color="error">{detailsError}</Typography>}
            </CardContent>
          </Card>
        </Grid>

        {/* Fantasy Platform IDs Section */}
        <Grid item xs={12} md={5}>
          <FantasyPlatformIdsForm
            matchId={match.id}
            initialValues={{
              d_id: match.d_id || 0,
              c_id: match.c_id || 0,
              mt_id: match.mt_id || 0,
              f_id: match.f_id || 0,
              v_id: match.v_id || 0,
              e_id: match.e_id || 0,
              e_o_id: match.e_o_id || 0,
            }}
          />
        </Grid>

        {/* Meta Details Section */}
        <Grid item xs={12} md={5}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>Meta Details</Typography>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}><b>Toss Winner:</b> {match.toss_winner?.name || 'N/A'}</Grid>
                <Grid item xs={12} sm={6}><b>Bat First:</b> {match.bat_first?.name || 'N/A'}</Grid>
                <Grid item xs={12} sm={6}><b>Match Winner:</b> {match.match_winner?.name || 'N/A'}</Grid>
                <Grid item xs={12} sm={6}><b>Weather:</b> {match.weather || 'N/A'}</Grid>
                <Grid item xs={12} sm={6}><b>Avg Score:</b> {match.avg_score ?? 'N/A'}</Grid>
                <Grid item xs={12} sm={6}><b>Avg Wickets:</b> {match.avg_wickets ?? 'N/A'}</Grid>
                <Grid item xs={12} sm={6}><b>Pacer Wickets:</b> {match.pacer_wickets ?? 'N/A'}</Grid>
                <Grid item xs={12} sm={6}><b>Spinner Wickets:</b> {match.spinner_wickets ?? 'N/A'}</Grid>
                <Grid item xs={12} sm={6}><b>Floodlit:</b> {match.floodlit || 'N/A'}</Grid>
                <Grid item xs={12} sm={6}><b>Rescheduled Date:</b> {match.rescheduled_date ? new Date(match.rescheduled_date).toLocaleString() : 'N/A'}</Grid>
                <Grid item xs={12} sm={6}><b>Created:</b> {new Date(match.created_at).toLocaleString()}</Grid>
                <Grid item xs={12} sm={6}><b>Updated:</b> {new Date(match.updated_at).toLocaleString()}</Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Contest Summaries Section */}
        {(match.d11_contest_summary || match.c_contest_summary || match.cb_contest_summary || match.mt_contest_summary) && (
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>Contest Summaries</Typography>
                <Grid container spacing={2}>
                  {match.d11_contest_summary && <Grid item xs={12} sm={6}><b>Dream11:</b> {match.d11_contest_summary}</Grid>}
                  {match.c_contest_summary && <Grid item xs={12} sm={6}><b>Circle11:</b> {match.c_contest_summary}</Grid>}
                  {match.cb_contest_summary && <Grid item xs={12} sm={6}><b>Cricbuzz11:</b> {match.cb_contest_summary}</Grid>}
                  {match.mt_contest_summary && <Grid item xs={12} sm={6}><b>MyTeam11:</b> {match.mt_contest_summary}</Grid>}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Innings Section */}
        {match.matchinning_set && match.matchinning_set.length > 0 && (
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>Match Innings</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Inning</TableCell>
                      <TableCell>Team</TableCell>
                      <TableCell>Runs</TableCell>
                      <TableCell>Wickets</TableCell>
                      <TableCell>Overs</TableCell>
                      <TableCell>Balls</TableCell>
                      <TableCell>Extras</TableCell>
                      <TableCell>Target</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {match.matchinning_set.map((inn: MatchInning) => (
                      <TableRow key={inn.id}>
                        <TableCell>{inn.inning}</TableCell>
                        <TableCell>{typeof inn.team === 'object' ? inn.team.name : inn.team}</TableCell>
                        <TableCell>{inn.runs}</TableCell>
                        <TableCell>{inn.wickets}</TableCell>
                        <TableCell>{inn.overs}</TableCell>
                        <TableCell>{inn.balls}</TableCell>
                        <TableCell>{inn.extras}</TableCell>
                        <TableCell>{inn.target}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Match Teams Section */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>Match Teams</Typography>
                <Button variant="contained" color="secondary" startIcon={<GroupIcon />} onClick={handleAddTeam}>
                  Add Team
                </Button>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Team Name</TableCell>
                    <TableCell>Is Home</TableCell>
                    <TableCell>Bat 1st Runs</TableCell>
                    <TableCell>Bat 1st Wkts</TableCell>
                    <TableCell>Bat 2nd Runs</TableCell>
                    <TableCell>Bat 2nd Wkts</TableCell>
                    <TableCell>Result</TableCell>
                    <TableCell>Prev Result</TableCell>
                    <TableCell>H2H Win</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {match.matchteam_set && match.matchteam_set.length > 0 ? match.matchteam_set.map((mt: MatchTeam) => (
                    <TableRow key={mt.id}>
                      <TableCell>{teams.find(t => t.id === mt.team)?.name || mt.team}</TableCell>
                      <TableCell>
                        {editingTeamId === mt.id ? (
                          <Switch
                            checked={!!editingTeamData.is_home}
                            onChange={e => handleChangeEditTeam('is_home', e.target.checked)}
                            color="primary"
                          />
                        ) : (mt.is_home ? 'Yes' : 'No')}
                      </TableCell>
                      <TableCell>
                        {editingTeamId === mt.id ? (
                          <TextField
                            size="small"
                            type="number"
                            value={editingTeamData.bat_first_runs ?? ''}
                            onChange={e => handleChangeEditTeam('bat_first_runs', e.target.value)}
                          />
                        ) : (mt.bat_first_runs ?? '-')}
                      </TableCell>
                      <TableCell>
                        {editingTeamId === mt.id ? (
                          <TextField
                            size="small"
                            type="number"
                            value={editingTeamData.bat_first_wickets ?? ''}
                            onChange={e => handleChangeEditTeam('bat_first_wickets', e.target.value)}
                          />
                        ) : (mt.bat_first_wickets ?? '-')}
                      </TableCell>
                      <TableCell>
                        {editingTeamId === mt.id ? (
                          <TextField
                            size="small"
                            type="number"
                            value={editingTeamData.bat_second_runs ?? ''}
                            onChange={e => handleChangeEditTeam('bat_second_runs', e.target.value)}
                          />
                        ) : (mt.bat_second_runs ?? '-')}
                      </TableCell>
                      <TableCell>
                        {editingTeamId === mt.id ? (
                          <TextField
                            size="small"
                            type="number"
                            value={editingTeamData.bat_second_wickets ?? ''}
                            onChange={e => handleChangeEditTeam('bat_second_wickets', e.target.value)}
                          />
                        ) : (mt.bat_second_wickets ?? '-')}
                      </TableCell>
                      <TableCell>
                        {editingTeamId === mt.id ? (
                          <TextField
                            size="small"
                            value={editingTeamData.result ?? ''}
                            onChange={e => handleChangeEditTeam('result', e.target.value)}
                          />
                        ) : (mt.result ?? '-')}
                      </TableCell>
                      <TableCell>
                        {editingTeamId === mt.id ? (
                          <TextField
                            size="small"
                            value={editingTeamData.previous_result ?? ''}
                            onChange={e => handleChangeEditTeam('previous_result', e.target.value)}
                          />
                        ) : (mt.previous_result ?? '-')}
                      </TableCell>
                      <TableCell>
                        {editingTeamId === mt.id ? (
                          <TextField
                            size="small"
                            type="number"
                            value={editingTeamData.h2h_win ?? ''}
                            onChange={e => handleChangeEditTeam('h2h_win', e.target.value)}
                          />
                        ) : (mt.h2h_win ?? '-')}
                      </TableCell>
                      <TableCell>
                        {editingTeamId === mt.id ? (
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton color="primary" size="small" onClick={handleSaveEditTeam} disabled={teamSaveLoading}>
                              <SaveIcon />
                            </IconButton>
                            <IconButton color="secondary" size="small" onClick={handleCancelEditTeam} disabled={teamSaveLoading}>
                              <CancelIcon />
                            </IconButton>
                          </Box>
                        ) : (
                          <IconButton color="primary" size="small" onClick={() => handleEditTeam(mt)}>
                            <EditIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow><TableCell colSpan={10}>No teams added yet.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
              {teamSaveError && <Typography color="error" variant="body2">{teamSaveError}</Typography>}
            </CardContent>
          </Card>
        </Grid>

        {/* After Match Teams Section */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>Match Players</Typography>
              <MatchPlayersTable matchId={match.id} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MatchView; 